import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Badge, Button } from '@vnetwork/ui/primitives';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import { Pencil } from 'lucide-react';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate, formatMoney } from '@/lib/format';
import { useServiceMetadata, type ServiceDetail } from '@/api/services';
import { OtherStandardScheduleDialog } from './other-standard-schedule-dialog';

const isDomain = (t: string) => t.startsWith('Domain.');
const isOther = (t: string) => t === 'Other.Standard';

/**
 * Per-template metadata card for templates whose config lives in
 * `/services/:id/metadata` rather than configoptions: Domain (registrant + DNS +
 * addons) and Other.Standard (payment schedule). Renders nothing for others.
 */
export function ServiceMetadataCard({ detail }: { detail: ServiceDetail }) {
  const relevant = isDomain(detail.service_template) || isOther(detail.service_template);
  const { data } = useServiceMetadata(detail.service_id, relevant);
  if (!relevant) return null;
  const meta = data?.metadata as Record<string, unknown> | undefined;
  if (!meta) return null;

  if (isOther(detail.service_template)) {
    return <OtherStandardCard meta={meta} serviceId={detail.service_id} currency={detail.service_currency || 'VND'} editable={detail.service_status === 'waiting-confirm'} />;
  }
  return <DomainCard meta={meta} />;
}

function DomainCard({ meta }: { meta: Record<string, unknown> }) {
  const extra = (meta.domain_extra_fields ?? {}) as Record<string, unknown>;
  const addons = (meta.addons as (string | null)[] | undefined)?.filter(Boolean) ?? [];
  const str = (v: unknown) => (typeof v === 'string' ? v : v && typeof v === 'object' && 'label' in v ? String((v as { label: unknown }).label) : v == null ? '' : String(v));

  const items: InfoItem[] = [
    { label: 'Domain', value: str(meta.domain_value) || '—' },
    { label: 'Type', value: str(meta.domain_type) || '—' },
    { label: 'DNS option', value: str(meta.dns_option) || '—' },
    { label: 'Send mail', value: meta.sendmail ? 'Yes' : 'No' },
    ...(meta.domain_eppcode ? [{ label: 'EPP code', value: str(meta.domain_eppcode) }] : []),
  ];
  // Registrant / owner fields (uiName/ownerName/…) — show the populated ones.
  const registrant = Object.entries(extra)
    .filter(([, v]) => str(v))
    .slice(0, 12)
    .map(([k, v]) => ({ label: prettify(k), value: str(v) }));

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Domain</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid items={items} />
        {addons.length ? (
          <div style={{ marginTop: 'var(--vn-space-3)', display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            {addons.map((a) => (
              <Badge key={String(a)} tone="info" appearance="subtle" size="sm">{prettify(String(a))}</Badge>
            ))}
          </div>
        ) : null}
        {registrant.length ? (
          <div style={{ marginTop: 'var(--vn-space-4)' }}>
            <CardTitle style={{ marginBottom: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>Registrant</CardTitle>
            <InfoGrid items={registrant} />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

interface Range { start_date?: string; end_date?: string; price?: number }

function OtherStandardCard({ meta, serviceId, currency = 'VND', editable }: { meta: Record<string, unknown>; serviceId: number; currency?: string; editable: boolean }) {
  const sched = (meta.other_standard_payment_schedule ?? {}) as { ranges?: Range[]; payment_mode?: string; payment_method?: string; description?: string };
  const ranges = sched.ranges ?? [];
  const [open, setOpen] = useState(false);
  const columns: ColumnDef<Range, unknown>[] = [
    { id: 'start_date', header: 'From', cell: ({ row }) => formatDate(row.original.start_date) },
    { id: 'end_date', header: 'To', cell: ({ row }) => formatDate(row.original.end_date) },
    { id: 'price', header: () => <span style={{ display: 'block', textAlign: 'right' }}>Price</span>, cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(row.original.price, currency)}</span> },
  ];
  return (
    <Card flat>
      <CardHeader actions={editable ? <Button variant="ghost" size="sm" onClick={() => setOpen(true)}><Pencil size={16} /> Edit</Button> : undefined}>
        <CardTitle>Payment schedule</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          {sched.payment_mode ? <Badge tone="neutral" appearance="subtle" size="sm">{prettify(sched.payment_mode)}</Badge> : null}
          {sched.payment_method ? <Badge tone="neutral" appearance="subtle" size="sm">{prettify(sched.payment_method)}</Badge> : null}
        </div>
        <DataTable columns={columns} data={ranges} getRowId={(_r, i) => String(i)} />
      </CardBody>
      {open ? (
        <OtherStandardScheduleDialog
          serviceId={serviceId}
          open={open}
          onClose={() => setOpen(false)}
          seed={{ payment_method: sched.payment_method, payment_mode: sched.payment_mode, description: sched.description, ranges: sched.ranges }}
        />
      ) : null}
    </Card>
  );
}

function prettify(s: string): string {
  return s.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
